import React, { useEffect, useMemo, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Holding, Currency, Settings, LiquidityEvent, Loan } from './types';
import { convertCurrency, getExchangeRates } from './utils/currency';
import { Navbar } from './components/Navbar';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectionsPage } from './pages/ProjectionsPage';
import { HoldingsPage } from './pages/HoldingsPage';
import { SettingsPage } from './pages/SettingsPage';
import { SnapshotsPage } from './pages/SnapshotsPage';
import { loansToLiquidityEvents } from './utils/loans';
import { OnboardingWizard, OnboardingData } from './components/OnboardingWizard';
import { toggleHoldingActive, deleteHolding, addRealEstateHolding, addLoanWithDeposit, deleteEvent as deleteEventService, recalculateBalances } from './utils/holdingsService';
const STORAGE_KEY_HOLDINGS = 'pairfolio-holdings';
const STORAGE_KEY_NAMES = 'pairfolio-partner-names';
const STORAGE_KEY_SETTINGS = 'pairfolio-settings';
const STORAGE_KEY_EVENTS = 'pairfolio-liquidity-events';
const STORAGE_KEY_SNAPSHOTS = 'pairfolio-snapshots';
const STORAGE_KEY_LOANS = 'pairfolio-loans';
const STORAGE_KEY_IS_DEMO = 'pairfolio-is-demo';
// Map new keys to legacy keys for data recovery
const LEGACY_KEY_MAP: Record<string, string> = {
  'pairfolio-holdings': 'duowealth-holdings',
  'pairfolio-partner-names': 'duowealth-partner-names',
  'pairfolio-settings': 'duowealth-settings',
  'pairfolio-liquidity-events': 'duowealth-liquidity-events'
};
function loadFromStorage<T>(key: string, fallback: T): T {
  try {
    // Always check legacy key first — if it exists, it has the user's real data
    // that hasn't been migrated yet (new keys may have been overwritten with defaults)
    const legacyKey = LEGACY_KEY_MAP[key];
    if (legacyKey) {
      const legacy = localStorage.getItem(legacyKey);
      if (legacy) {
        const parsed = JSON.parse(legacy);
        // Migrate: write to new key and remove old
        localStorage.setItem(key, legacy);
        localStorage.removeItem(legacyKey);
        return parsed;
      }
    }
    // No legacy data — use new key
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch {
    return fallback;
  }
}
export function App() {
  const [partnerNames, setPartnerNames] = useState<[string, string]>(() => loadFromStorage<[string, string]>(STORAGE_KEY_NAMES, ['Partner 1', 'Partner 2']));
  const [settings, setSettings] = useState<Settings>(() => loadFromStorage<Settings>(STORAGE_KEY_SETTINGS, {
    baseCurrency: 'USD',
    withdrawalStrategy: 'drawdown',
    partnerDOBs: ['', ''],
    drawdownReturnRate: 4,
    monthlyContribution: 0,
    timeHorizon: 30,
    desiredDrawdown: 5000,
    inflationEnabled: false,
    inflationRate: 3
  }));
  const baseCurrency = settings.baseCurrency ?? 'USD';
  const setBaseCurrency = (currency: Currency) => {
    setSettings(prev => ({
      ...prev,
      baseCurrency: currency
    }));
  };
  const [holdings, setHoldings] = useState<Holding[]>(() => loadFromStorage<Holding[]>(STORAGE_KEY_HOLDINGS, []));
  const [liquidityEvents, setLiquidityEvents] = useState<LiquidityEvent[]>(() => loadFromStorage<LiquidityEvent[]>(STORAGE_KEY_EVENTS, []));
  const [loans, setLoans] = useState<any[]>(() => loadFromStorage<any[]>(STORAGE_KEY_LOANS, []));
  const [snapshots, setSnapshots] = useState<any[]>(() => loadFromStorage<any[]>(STORAGE_KEY_SNAPSHOTS, []));
  const [isDemo, setIsDemo] = useState<boolean>(() => loadFromStorage<boolean>(STORAGE_KEY_IS_DEMO, false));
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_HOLDINGS, JSON.stringify(holdings));
  }, [holdings]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_LOANS, JSON.stringify(loans));
  }, [loans]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_SNAPSHOTS, JSON.stringify(snapshots));
  }, [snapshots]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_EVENTS, JSON.stringify(liquidityEvents));
  }, [liquidityEvents]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_NAMES, JSON.stringify(partnerNames));
  }, [partnerNames]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_SETTINGS, JSON.stringify(settings));
  }, [settings]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_IS_DEMO, JSON.stringify(isDemo));
  }, [isDemo]);
  const handleSaveNames = (newNames: [string, string]) => {
    const oldNames = partnerNames;
    setPartnerNames(newNames);
    // Update existing holdings to use new names (skip Joint holdings)
    setHoldings(prev => prev.map(h => {
      if (h.person === 'Joint') return h;
      if (h.person === oldNames[0]) return {
        ...h,
        person: newNames[0]
      };
      if (h.person === oldNames[1]) return {
        ...h,
        person: newNames[1]
      };
      return h;
    }));
  };
  const handleAddHolding = (newHolding: Omit<Holding, 'id' | 'createdAt' | 'history'>) => {
    if (newHolding.isRealEstate) {
      const result = addRealEstateHolding(holdings, liquidityEvents, newHolding);
      setHoldings(result.holdings);
      setLiquidityEvents(result.events);
    } else {
      const now = new Date().toISOString();
      const holdingId = Math.random().toString(36).substr(2, 9);
      const holding: Holding = {
        ...newHolding,
        id: holdingId,
        createdAt: now,
        history: [{
          amount: newHolding.amount,
          date: now
        }]
      };
      setHoldings(prev => [...prev, holding]);
    }
  };
  const handleUpdateHoldingAmount = (id: string, newAmount: number) => {
    setHoldings(prev => prev.map(h => {
      if (h.id === id) {
        const now = new Date().toISOString();
        const currentHistory = h.history || [{
          amount: h.amount,
          date: h.createdAt || now
        }];
        return {
          ...h,
          amount: newAmount,
          history: [...currentHistory, {
            amount: newAmount,
            date: now
          }]
        };
      }
      return h;
    }));
  };
  const handleRenameHolding = (id: string, newName: string) => {
    setHoldings(prev => prev.map(h => h.id === id ? {
      ...h,
      name: newName
    } : h));
  };
  const handleUpdateCountry = (id: string, newCountry: string) => {
    setHoldings(prev => prev.map(h => h.id === id ? {
      ...h,
      country: newCountry
    } : h));
  };
  const handleUpdateHoldingFull = (id: string, updates: Partial<Omit<Holding, 'id' | 'createdAt' | 'history'>>) => {
    const holding = holdings.find(h => h.id === id);
    if (!holding) return;
    // Update holding (single pass)
    setHoldings(prev => prev.map(h => {
      if (h.id !== id) return h;
      const now = new Date().toISOString();
      const currentHistory = h.history || [{
        amount: h.amount,
        date: h.createdAt || now
      }];
      const newHistory = updates.amount !== undefined && updates.amount !== h.amount ? [...currentHistory, {
        amount: updates.amount,
        date: now
      }] : currentHistory;
      return {
        ...h,
        ...updates,
        history: newHistory
      };
    }));
    // Update associated auto-created events (single pass)
    if (holding.isRealEstate) {
      setLiquidityEvents(prev => prev.map(e => {
        if (!holding.autoCreatedEventIds?.includes(e.id)) return e;
        if (e.name.includes('Rental Income') && updates.rentalIncome !== undefined) {
          return {
            ...e,
            name: `${updates.name || holding.name} Rental Income`,
            amount: updates.rentalIncome,
            currency: updates.currency || holding.currency
          };
        }
        if (e.name.includes('Closing Costs') && updates.closingCosts !== undefined) {
          return {
            ...e,
            name: `${updates.name || holding.name} Closing Costs`,
            amount: -updates.closingCosts,
            currency: updates.currency || holding.currency
          };
        }
        return e;
      }));
    }
  };
  const handleRecalculate = (): number => {
    const result = recalculateBalances(holdings, liquidityEvents, loans);
    setHoldings(result.holdings);
    setLiquidityEvents(result.events);
    setLoans(result.loans);
    return result.corrections;
  };
  const handleToggleHoldingActive = (id: string) => {
    const result = toggleHoldingActive(holdings, liquidityEvents, loans, id);
    setHoldings(result.holdings);
    setLiquidityEvents(result.events);
    setLoans(result.loans);
  };
  const handleDeleteHolding = (id: string) => {
    const result = deleteHolding(holdings, liquidityEvents, loans, id);
    setHoldings(result.holdings);
    setLiquidityEvents(result.events);
    setLoans(result.loans);
  };
  const handleAddEvent = (event: Omit<LiquidityEvent, 'id'>): string => {
    const eventId = Math.random().toString(36).substr(2, 9);
    setLiquidityEvents(prev => [...prev, {
      ...event,
      id: eventId
    }]);
    return eventId;
  };
  const handleDeleteEvent = (id: string) => {
    const result = deleteEventService(holdings, liquidityEvents, id);
    setHoldings(result.holdings);
    setLiquidityEvents(result.events);
  };
  const handleUpdateEvent = (id: string, updates: Partial<Omit<LiquidityEvent, 'id'>>) => {
    setLiquidityEvents(prev => prev.map(e => e.id === id ? {
      ...e,
      ...updates
    } : e));
  };
  const handleToggleEventActive = (id: string) => {
    setLiquidityEvents(prev => prev.map(e => e.id === id ? {
      ...e,
      isActive: !(e.isActive ?? true)
    } : e));
  };
  const handleCreateSnapshot = (name: string) => {
    const newSnapshot = {
      id: Math.random().toString(36).substr(2, 9),
      name,
      createdAt: new Date().toISOString(),
      holdings: JSON.parse(JSON.stringify(holdings)),
      settings: JSON.parse(JSON.stringify(settings)),
      partnerNames: [...partnerNames],
      liquidityEvents: JSON.parse(JSON.stringify(liquidityEvents)),
      loans: JSON.parse(JSON.stringify(loans)),
      exchangeRates: {
        ...getExchangeRates()
      }
    };
    setSnapshots(prev => [newSnapshot, ...prev]);
  };
  const handleDeleteSnapshot = (id: string) => {
    setSnapshots(prev => prev.filter(s => s.id !== id));
  };
  const handleAddLoan = (newLoan: Omit<Loan, 'id' | 'createdAt'>): string => {
    const result = addLoanWithDeposit(holdings, liquidityEvents, loans, newLoan as Omit<Loan, 'id' | 'createdAt'>);
    setHoldings(result.holdings);
    setLiquidityEvents(result.events);
    setLoans(result.loans);
    return result.loanId;
  };
  const handleDeleteLoan = (id: string) => {
    setLoans(prev => prev.filter(l => l.id !== id));
  };
  const handleUpdateLoan = (id: string, updates: Partial<Omit<Loan, 'id' | 'createdAt'>>) => {
    setLoans(prev => prev.map(l => l.id === id ? {
      ...l,
      ...updates
    } : l));
  };
  const handleToggleLoanActive = (id: string) => {
    setLoans(prev => prev.map(l => l.id === id ? {
      ...l,
      isActive: !l.isActive
    } : l));
  };
  const totalLiquidInBase = useMemo(() => {
    return holdings.filter(h => h.type === 'Liquid' && (h.isActive ?? true)).reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, baseCurrency), 0);
  }, [holdings, baseCurrency]);
  const totalIlliquidInBase = useMemo(() => {
    return holdings.filter(h => h.type === 'Illiquid' && (h.isActive ?? true)).reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, baseCurrency), 0);
  }, [holdings, baseCurrency]);
  // Merge user liquidity events with generated loan events
  // Only include active events and active loans in projections
  const combinedLiquidityEvents = useMemo(() => {
    const activeEvents = liquidityEvents.filter(e => e.isActive ?? true);
    const loanEvents = loansToLiquidityEvents(loans);
    return [...activeEvents, ...loanEvents];
  }, [liquidityEvents, loans]);
  const showOnboarding = holdings.length === 0 && partnerNames[0] === 'Partner 1' && partnerNames[1] === 'Partner 2';
  if (showOnboarding) {
    return <OnboardingWizard onComplete={(data: OnboardingData) => {
      window.location.hash = '#/';
      setPartnerNames(data.partnerNames);
      setSettings(data.settings);
      setHoldings(data.holdings);
      setLiquidityEvents(data.liquidityEvents);
      setLoans(data.loans);
      setIsDemo(data.isDemo ?? false);
    }} data-id="element-0" />;
  }
  return <Router data-id="element-1">
      <div className="min-h-screen bg-gray-50/50 text-slate-900 font-sans flex flex-col" data-id="element-2">
        <Navbar baseCurrency={baseCurrency} setBaseCurrency={setBaseCurrency} holdings={holdings} data-id="element-3" />

        <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-4">
          <Routes data-id="element-5">
            <Route path="/" element={<DashboardPage holdings={holdings} baseCurrency={baseCurrency} partnerNames={partnerNames} loans={loans} liquidityEvents={liquidityEvents} monthlyContribution={settings.monthlyContribution} onRecalculate={handleRecalculate} isDemo={isDemo} onDismissDemo={() => setIsDemo(false)} data-id="element-7" />} data-id="element-6" />
            <Route path="/projections" element={<ProjectionsPage holdings={holdings} baseCurrency={baseCurrency} partnerNames={partnerNames} totalLiquidInBase={totalLiquidInBase} totalIlliquidInBase={totalIlliquidInBase} settings={settings} onUpdateSettings={setSettings} liquidityEvents={combinedLiquidityEvents} snapshots={snapshots} data-id="element-9" />} data-id="element-8" />
            <Route path="/holdings" element={<HoldingsPage holdings={holdings} partnerNames={partnerNames} baseCurrency={baseCurrency} baseCountry={settings.baseCountry || 'US'} onAddHolding={handleAddHolding} onUpdateHoldingAmount={handleUpdateHoldingAmount} onRenameHolding={handleRenameHolding} onUpdateCountry={handleUpdateCountry} onUpdateHoldingFull={handleUpdateHoldingFull} onToggleHoldingActive={handleToggleHoldingActive} onDeleteHolding={handleDeleteHolding} liquidityEvents={liquidityEvents} onAddEvent={handleAddEvent} onDeleteEvent={handleDeleteEvent} onUpdateEvent={handleUpdateEvent} onToggleEventActive={handleToggleEventActive} loans={loans} onAddLoan={handleAddLoan} onDeleteLoan={handleDeleteLoan} onUpdateLoan={handleUpdateLoan} onToggleLoanActive={handleToggleLoanActive} data-id="element-11" />} data-id="element-10" />
            <Route path="/settings" element={<SettingsPage baseCurrency={baseCurrency} onSetBaseCurrency={setBaseCurrency} partnerNames={partnerNames} onSaveNames={handleSaveNames} settings={settings} onUpdateSettings={setSettings} onResetAll={() => {
            setHoldings([]);
            setLiquidityEvents([]);
            setLoans([]);
            setSnapshots([]);
            setIsDemo(false);
            setPartnerNames(['Partner 1', 'Partner 2']);
            setSettings({
              baseCurrency: 'USD',
              baseCountry: 'US',
              withdrawalStrategy: 'drawdown',
              partnerDOBs: ['', ''],
              drawdownReturnRate: 4,
              monthlyContribution: 0,
              timeHorizon: 30,
              desiredDrawdown: 5000,
              inflationEnabled: false,
              inflationRate: 3
            });
          }} data-id="element-13" />} data-id="element-12" />
            <Route path="/snapshots" element={<SnapshotsPage snapshots={snapshots} onCreateSnapshot={handleCreateSnapshot} onDeleteSnapshot={handleDeleteSnapshot} onImportSnapshot={snapshot => setSnapshots(prev => [snapshot, ...prev])} onRestoreSnapshot={snapshot => {
            setHoldings(JSON.parse(JSON.stringify(snapshot.holdings)));
            setSettings(JSON.parse(JSON.stringify(snapshot.settings)));
            setPartnerNames([...snapshot.partnerNames]);
            setLiquidityEvents(JSON.parse(JSON.stringify(snapshot.liquidityEvents)));
            setLoans(JSON.parse(JSON.stringify(snapshot.loans || [])));
          }} currentData={{
            holdings,
            settings,
            partnerNames,
            liquidityEvents,
            loans,
            exchangeRates: getExchangeRates()
          }} data-id="element-15" />} data-id="element-14" />
            <Route path="*" element={<Navigate to="/" replace data-id="element-17" />} data-id="element-16" />
          </Routes>
        </main>
      </div>
    </Router>;
}